/* eslint-disable react/react-in-jsx-scope */
import i18n from "@/i18n";
import { FC, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Switch } from "@/components/ui/switch";
import { toast } from "@/components/ui/use-toast";
import usePostData from "@/hooks/usePostData";
import { TriangleAlert } from "lucide-react";

const StatCardColoredProgram = ({
  showWarningFinancement,
  totalFinancement,
  totalSuivi,
  indicators,
  contributionObtenu,
  appelAContribution,
  allIndicators = [],
  context = null,
}) => {
  const { t } = useTranslation("dashboard", { keyPrefix: "program" });
  const [totalFin, setTotalFin] = useState(
    contributionObtenu + appelAContribution
  );

  useEffect(() => {
    setTotalFin(contributionObtenu + appelAContribution);
  }, [contributionObtenu, appelAContribution]);

  const colorClasses = [
    "text-verdeoorange bg-verdeoorange",
    "text-verdeoblue bg-verdeoblue",
    "text-verdeogray bg-verdeogray",
    "text-verdeored bg-verdeored",
    "text-verdeogreen bg-verdeogreen",
  ];
  const [isSheetOpen, setIsSheetOpen] = useState(false);

  const { postData } = usePostData<any>();

  // On ne filtre plus par prog_id/dashboard/programme, seulement sur le code "euro_recus"
  const filteredIndicators =
    Array.from(
      new Map(
        indicators
          ?.filter((ind) => ind.indicator.code !== "euro_recus")
          .map((ind) => [ind.indicator.id, ind])
      ).values()
    ) || [];

  const sheetIndicator = allIndicators;

  return (
    <>
      <Sheet open={isSheetOpen} onOpenChange={setIsSheetOpen}>
        <SheetContent>
          <SheetHeader>
            <SheetTitle>Modification</SheetTitle>
            <SheetDescription></SheetDescription>
          </SheetHeader>
          {sheetIndicator?.map((ind, index: number) => {
            const [open, setOpen] = useState(false);
            const [programmeSwitch, setProgrammeSwitch] = useState(
              ind.programme ?? true
            );

            const handleCancel = () => {
              setProgrammeSwitch(ind.programme ?? true);
              setOpen(false);
            };

            const currentItem = {
              id: ind.id,
              name: ind.name,
              unity: ind.unity,
              esrs: ind.esrs,
              cumulative: ind.cumulative,
              graphe: ind.graphe,
              programme: programmeSwitch,
            };

            const payload = {
              currentItem: {
                ...currentItem,
                cumulative: Boolean(currentItem.cumulative),
                graphe: Boolean(currentItem.graphe),
                programme: Boolean(currentItem.programme),
              },
              cumulative: Boolean(currentItem.cumulative),
              graphe: Boolean(currentItem.graphe),
              programme: Boolean(currentItem.programme),
            };

            const handleSave = async () => {
              await postData({
                url: `${import.meta.env.VITE_API_ENDPOINT
                  }/parameter-indicators/crupdate`,
                data: payload,
              });
              setOpen(false);
              toast({
                title: "Paramètres sauvegardés",
                description: "Les changements ont été enregistrés avec succès",
              });
              window.location.reload();
            };

            return (
              <div key={ind.id} className="flex flex-col border-b pb-2 mt-5">
                <div
                  className="flex items-center space-x-2 cursor-pointer"
                  onClick={() => setOpen((prev) => !prev)}
                >
                  <div className="flex-1">
                    <Label>{ind.name}</Label>
                  </div>
                  <span className="text-xs text-muted-foreground">
                    {open ? "▲" : "▼"}
                  </span>
                </div>
                {open && (
                  <div className="mt-2 flex flex-col space-y-3 overflow-y-auto">
                    <div className="col-span-2 space-y-4">
                      <div className="border rounded-lg p-3 flex items-center justify-between">
                        <Label>Afficher sur la fiche programme :</Label>
                        <Switch
                          checked={programmeSwitch}
                          onCheckedChange={setProgrammeSwitch}
                        />
                      </div>
                    </div>
                    <div className="flex flex-col-reverse sm:flex-row gap-2 sm:gap-4 justify-end w-full mt-4">
                      <Button
                        variant="outline"
                        className="w-full sm:w-auto"
                        onClick={handleCancel}
                      >
                        Annuler
                      </Button>
                      <Button className="w-full sm:w-auto" onClick={handleSave}>
                        Sauvegarder
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </SheetContent>
      </Sheet>

      <div className="flex flex-wrap gap-y-4">
        {/* <div key={"ctp"} className="basis-1/3 px-2">
          <CardColored
            className={colorClasses[0 % colorClasses.length]}
            title={t("Coût total prévu")}
            value={`${
              isNaN(totalFin) || totalFin === null ? 0 : totalFin.toFixed(1)
            }`}
            desc={
              contributionObtenu && !isNaN(contributionObtenu)
                ? contributionObtenu.toFixed(1)
                : 0
            }
            showWarningFinancement={false}
            totalFinancement={totalFinancement}
            totalSuivi={totalSuivi}
          />
        </div> */}

        {filteredIndicators?.filter(
          (ind) =>
            ind.indicator.code !== "euro_recus" &&
            (
              context === null ||
              (context === "dashboard" && ind.indicator.dashboard === true) ||
              (context === "programme" && ind.indicator.programme === true)
            )
        )
          .map((ind, index) => {
            const colorClass = colorClasses[(index + 1) % colorClasses.length];
            const plannedIndicator = indicators?.find(
              (i: any) => i.indicator.id === ind.indicator.id && i.type === 1,
            );
            const unity = plannedIndicator?.indicator.unity;
            const plannedValue = context === "programme" ?
              (indicators.find(
                (i: any) =>
                  i.indicator.id === ind.indicator.id && i.type === 1
              )?.total || 0) :
              (ind.indicator.moyenne && plannedIndicator?.count
                ? (plannedIndicator?.total || 0) / plannedIndicator.count
                : plannedIndicator?.total || 0);

            const realizedIndicator = indicators.find(
              (i: any) => i.indicator.id === ind.indicator.id && i.type === 2,
            );

            const realizedValue = context === "programme" ?
              (indicators.find(
                (i: any) =>
                  i.indicator.id === ind.indicator.id && i.type === 2
              )?.total || 0) :
              (ind.indicator.moyenne && realizedIndicator?.count
                ? (realizedIndicator?.total || 0) / realizedIndicator.count
                : realizedIndicator?.total || 0);
            return (
              <div key={index} className="basis-1/3 px-2">
                <CardColored
                  className={colorClass}
                  title={`${ind.indicator.indicatorLangs?.find(
                    (ptl) => ptl.lang.code === i18n.language
                  )?.name || ind.indicator.name
                    }`}
                  value={plannedValue}
                  desc={realizedValue}
                  // value={
                  //   indicators?.find(
                  //     (i: any) =>
                  //       i.indicator.id === ind.indicator.id && i.type === 1
                  //   )?.total || 0
                  // }
                  // desc={
                  //   indicators.find(
                  //     (i: any) =>
                  //       i.indicator.id === ind.indicator.id && i.type === 2
                  //   )?.total || 0
                  // }
                  showWarningFinancement={
                    ind.indicator.code === "euro_recus" && showWarningFinancement
                  }
                  totalFinancement={totalFinancement}
                  totalSuivi={totalSuivi}
                />
              </div>
            );
          })}
      </div>
    </>
  );
};

const CardColored: FC<{
  showWarningFinancement?: boolean;
  totalFinancement?: number;
  totalSuivi?: number;
  title: string;
  value: number;
  desc: number;
  className: string;
}> = ({
  showWarningFinancement = false,
  totalFinancement = 0,
  totalSuivi = 0,
  title,
  value,
  desc,
  className,
}) => {
    const { t } = useTranslation("dashboard", { keyPrefix: "program" });

    return (
      <div
        className={`rounded-[11px] px-[10px] py-[13px] overflow-hidden ${className.split(" ")[0]
          } relative`}
        title={title}
      >
        <div className="relative z-10">
          <h3 className="mb-2 text-[14px] font-semibold line-clamp-2">
            {title} {t("prévu")}
          </h3>
          <p className="mb-1 text-[20px] font-semibold">
            {Math.round(Number(value)).toLocaleString("fr-FR")}
          </p>
          <div className="flex gap-2 justify-between">
            <p className="mb-0 text-[12px]">
              {t("Déjà réalisé")} :{" "}
              {Math.round(Number(desc)).toLocaleString("fr-FR")}
            </p>
            {showWarningFinancement && (
              <Popover>
                <PopoverTrigger asChild>
                  <TriangleAlert
                    className="cursor-pointer"
                    size={15}
                    color="#FF0000"
                  />
                </PopoverTrigger>
                <PopoverContent className="text-xs">
                  {t(
                    "Une différence a été constatée entre les financements saisis et les indicateurs de suivi des versements. Merci de vérifier et de corriger."
                  )}{" "}
                  <br />
                  {t(
                    "Pour rappel vous avez déclaré en financements souhaités + ceux déjà obtenus ="
                  )}{" "}
                  <b>{Math.round(totalFinancement).toLocaleString("fr-FR")} €</b>{" "}
                  <br />
                  {t("Ceux enregistrés sur vos tableaux de suivi =")}{" "}
                  <b>{Math.round(totalSuivi).toLocaleString("fr-FR")} €</b>
                </PopoverContent>
              </Popover>
            )}
          </div>
        </div>
        <div
          className={`${className.split(" ")[1]
            } opacity-10 absolute top-0 left-0 w-full h-full`}
        ></div>
      </div>
    );
  };

export default StatCardColoredProgram;
